package com.evoapps.evoshift.domain.model

import kotlinx.serialization.Serializable

/**
 * SH-135: hours of one day, "HH:MM" to "HH:MM" ("24:00" is the end of the day). A range whose end
 * is not after its start is ignored.
 */
@Serializable
data class HoursRange(val start: String, val end: String) {
    /** Minutes from midnight of [start] and [end]; null when either cannot be read. */
    val minutes: IntRange? get() {
        val from = minutesOf(start) ?: return null
        val to = minutesOf(end) ?: return null
        return if (to > from) from until to else null
    }

    companion object {
        /** "HH:MM" (or "H:MM") to minutes from midnight, 0..1440; null when it is not a time. */
        fun minutesOf(time: String): Int? {
            val parts = time.trim().split(':')
            if (parts.size != 2) return null
            val h = parts[0].toIntOrNull() ?: return null
            val m = parts[1].toIntOrNull() ?: return null
            if (h !in 0..24 || m !in 0..59 || (h == 24 && m != 0)) return null
            return h * 60 + m
        }
    }
}

/**
 * SH-135: one day of a person's availability, the same for every workplace they work at.
 *
 * - [ranges]: the hours they can work; empty means the whole day.
 * - [preferred]: the hours they would rather work (within [ranges]); empty means no preference.
 * - [comment]: seen by every workplace the availability goes to.
 * - [workplaceComments]: a comment for one workplace only (workplace id to text).
 */
@Serializable
data class AvailabilityDay(
    val status: AvailabilityStatus,
    val ranges: List<HoursRange> = emptyList(),
    val preferred: List<HoursRange> = emptyList(),
    val comment: String? = null,
    val workplaceComments: Map<String, String> = emptyMap()
)

/**
 * SH-135: a person's availability for a month, given once for all their workplaces
 * (`users/{uid}/availability/{YYYY-MM}`, only the person reads and writes it). Each workplace gets
 * its own copy mapped onto its slots ([Availability] in `availability`, what its managers read),
 * see [com.evoapps.evoshift.domain.availability.AvailabilitySlotMapping].
 *
 * [releasedToOwnerIds]: the employers the person sent it to themselves (by filling in the form at
 * one of their workplaces). Workplaces of those owners always get it; other owners' workplaces
 * only when the person shares it with all employers ([AvailabilitySharing]).
 */
@Serializable
data class SharedAvailability(
    val userId: String,
    val month: String, // "YYYY-MM"
    val days: Map<Int, AvailabilityDay>,
    val isFilled: Boolean = false,
    val releasedToOwnerIds: Set<String> = emptySet(),
    val updatedAt: String? = null
)

/**
 * SH-135: the person's choice whether their availability goes to every employer. Workplaces of
 * the same owner always share it. Null [shareWithAllEmployers]: never asked (treated as no).
 */
@Serializable
data class AvailabilitySharing(
    val shareWithAllEmployers: Boolean? = null,
    val updatedAt: String? = null
) {
    val sharesWithAll: Boolean get() = shareWithAllEmployers == true
    val wasAsked: Boolean get() = shareWithAllEmployers != null
}
